import type { RefObject } from 'react';
import { fadeTo } from 'util/animation/fade/fade';
import { plotPoints } from 'util/geometry.utils';
import { findTransitionController } from '../../../util/core-transition-component';
import { splitElement } from '../../../util/animation/split/split';
import { originalSize } from './hooks/useBackgroundFan';

interface HeaderShowreelRefs {
  elementRef?: RefObject<HTMLElement | null>;
  title1Ref?: RefObject<HTMLHeadingElement | null>;
  title2Ref?: RefObject<HTMLHeadingElement | null>;

  title1MaskRef?: RefObject<HTMLElement | null>;
  title2MaskRef?: RefObject<HTMLHeadingElement | null>;

  maskedContainerRef?: RefObject<HTMLElement | null>;
  shapeContainerRef?: RefObject<HTMLElement | null>;

  playButtonRef?: RefObject<HTMLButtonElement | null>;

  siteHeaderElementRef?: RefObject<HTMLElement | null>;
}

export function setupTransitionInTimeline(
  timeline: gsap.core.Timeline,
  {
    title1Ref,
    title2Ref,
    maskedContainerRef,
    shapeContainerRef,
    title1MaskRef,
    title2MaskRef,
    playButtonRef,
  }: HeaderShowreelRefs,
): void {
  const title1Controller = findTransitionController(title1Ref);
  const title2Controller = findTransitionController(title2Ref);
  const playButtonController = findTransitionController(playButtonRef);
  const titleHeight = title1Ref?.current?.offsetHeight;
  const titleOffset = title1Ref?.current?.offsetTop || 0;
  const shapeContainerHeight = shapeContainerRef?.current?.offsetHeight;
  let titleOffsetY = 0;

  timeline.add(title1Controller?.getTimeline('in') as gsap.core.Timeline, 0);
  timeline.add(title2Controller?.getTimeline('in') as gsap.core.Timeline, 0.1);

  timeline.addLabel('beforeTitleExpand', '-=0');

  if (shapeContainerHeight && titleHeight) {
    titleOffsetY = shapeContainerHeight / 2 - titleHeight - titleOffset * 1.2;
  }

  if (title1Ref && title2Ref && title1MaskRef && title2MaskRef) {
    [title1MaskRef, title2MaskRef].forEach((ref) => {
      splitElement(
        ref.current as HTMLElement,
        'chars',
        {
          linesClass: 'mask-split-line',
        },
        true,
      );
    });

    timeline.fromTo(
      [title1Ref.current, title1MaskRef.current],
      { y: titleOffsetY },
      { y: 0, duration: 0.5, ease: 'power3.easeIn' },
      'beforeTitleExpand',
    );
    timeline.fromTo(
      [title2Ref.current, title2MaskRef.current],
      { y: -titleOffsetY },
      { y: 0, duration: 0.5, ease: 'power3.easeIn' },
      'beforeTitleExpand',
    );
  }

  if (maskedContainerRef?.current && shapeContainerRef?.current) {
    const centerX = 0.5 * window.innerWidth;
    const bounds = shapeContainerRef.current.getBoundingClientRect();
    const wrapperBounds = maskedContainerRef.current.getBoundingClientRect();

    const xPosition = bounds.left + bounds.width / 2;
    const yPosition =
      bounds.top - wrapperBounds.top + bounds.height * (originalSize.y0 / originalSize.h);
    const vertical = plotPoints(bounds.width * 0.5, 4, xPosition, yPosition);
    const side = plotPoints(bounds.width, 4, xPosition, yPosition);

    timeline.fromTo(
      maskedContainerRef.current,
      {
        clipPath: `polygon(
          ${centerX}px ${vertical[1].y}px, 
          ${centerX}px ${vertical[1].y}px, 
          ${centerX}px ${vertical[3].y}px, 
          ${centerX}px ${vertical[3].y}px)`,
      },
      {
        duration: 1,
        ease: 'Expo.easeInOut',
        clipPath: `polygon(
          ${side[2].x}px ${vertical[1].y}px, 
          ${side[0].x}px ${vertical[1].y}px, 
          ${centerX}px ${vertical[3].y}px,
           ${centerX}px ${vertical[3].y}px)`,
        clearProps: 'clipPath',
      },
      '-=0.6',
    );
  }

  if (playButtonController) {
    timeline.add(playButtonController.getTimeline('in') as gsap.core.Timeline, '-=0.4');
  }
}

export function setupTransitionOutTimeline(
  timeline: gsap.core.Timeline,
  { elementRef }: HeaderShowreelRefs,
): void {
  if (elementRef) {
    timeline.add(fadeTo(elementRef.current as HTMLElement));
  }
}

export function enterFullScreenTransition(
  timeline: gsap.core.Timeline,
  {
    title1MaskRef,
    title2MaskRef,
    playButtonRef,
    title1Ref,
    title2Ref,
    siteHeaderElementRef,
  }: HeaderShowreelRefs,
): void {
  const playButtonController = findTransitionController(playButtonRef);

  if (title1MaskRef && title2MaskRef && title1Ref && title2Ref) {
    timeline.add(
      fadeTo([title1MaskRef.current, title2MaskRef.current] as Array<HTMLElement>, {
        duration: 0.2,
      }),
    );
    timeline.add(
      fadeTo([title1Ref.current, title2Ref.current] as Array<HTMLElement>, {
        duration: 0.2,
      }),
      0.3,
    );
    timeline.add(playButtonController?.getTimeline('out') as gsap.core.Timeline, 0);
  }

  if (siteHeaderElementRef?.current) {
    timeline.add(
      fadeTo(siteHeaderElementRef?.current, {
        duration: 0.2,
      }),
      0,
    );
  }
}

export function exitFullScreenTransition(
  timeline: gsap.core.Timeline,
  {
    title1MaskRef,
    title2MaskRef,
    playButtonRef,
    title1Ref,
    title2Ref,
    siteHeaderElementRef,
  }: HeaderShowreelRefs,
): void {
  const playButtonController = findTransitionController(playButtonRef);

  if (title1MaskRef && title2MaskRef && title1Ref && title2Ref) {
    timeline.add(
      fadeTo(
        [
          title1MaskRef.current,
          title2MaskRef.current,
          title1Ref.current,
          title2Ref.current,
        ] as Array<HTMLElement>,
        {
          to: 1,
          duration: 0.3,
        },
      ),
    );
    timeline.add(playButtonController?.getTimeline('in') as gsap.core.Timeline, 0.2);
  }

  if (siteHeaderElementRef?.current) {
    timeline.add(
      fadeTo(siteHeaderElementRef?.current, {
        to: 1,
        duration: 0.3,
      }),
      0,
    );
  }
}

export function scrollOutTransition(
  timeline: gsap.core.Timeline,
  { title1MaskRef, title2MaskRef, title1Ref, title2Ref }: HeaderShowreelRefs,
): void {
  if (title1MaskRef && title2MaskRef && title1Ref && title2Ref) {
    timeline.to(
      [
        title1MaskRef.current,
        title2MaskRef.current,
        title1Ref.current,
        title2Ref.current,
      ] as Array<HTMLElement>,
      {
        yPercent: 50,
      },
    );
  }
}
